import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Text, Input, Button, Flex, Form, Grid } from '@pega/cosmos-react-core';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { appState } from '../../../interfaces/common';
import { addNewApiConnection } from './ThirdPartyConnectionApis';
import useLocalTranslation from '../../../hooks/useLocalTranslation';

interface APIFormData {
  name: string;
  clientWebhook: string;
}

const CreateAPI: React.FC = () => {
  const { register, handleSubmit, errors, reset, formState } = useForm<APIFormData>({ mode: 'all' });
  const sessionId = useSelector((state: appState) => state.sessionId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();

  const onSubmit = async (data: APIFormData) => {
    const payload = {
      accountName: data.name,
      webhook: data.clientWebhook,
    };

    if (formState.touched) {
      dispatch(updateLoaderDetails(true));
      const json = await addNewApiConnection(sessionId, payload);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        reset();
        navigate(`/custom/${json?.data?.accountId}`);
        dispatch(updateBannerInfo(`${translate('third_party')} ${translate('account_registered')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        let errorMessage;
        if (json?.data?.code) {
          errorMessage = translate(json.data.code);
        }
        dispatch(updateBannerInfo(errorMessage || translate('custom_account_creation_failure'), 'urgent'));
      }
    }
  };

  const actions = (
    <Flex>
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const headerElement = () => {
    return (
      <Text variant="h1" as="h1">
        {`${translate('new')} ${translate('Client Channel API')}`}
      </Text>
    );
  };

  const domainValidation = (url: string) => {
    const validationMsg: string = translate('domain_validation_message');

    const pattern = new RegExp(
      '^(https:\\/\\/)' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})(/[-\\w@\\+\\.~#\\?&/=%]*)?$' + // domain
        '(\\:\\d+)?$', // port - optional
      'i',
    );

    return !!pattern.test(url) ? true : validationMsg;
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      <Grid
        container={{
          cols: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        <Form actions={actions} className="basic-form" onSubmit={handleSubmit(onSubmit)}>
          <Input
            name="name"
            label={translate('name')}
            required={true}
            autoComplete="off"
            info={errors.name?.message}
            status={errors.name?.message ? 'error' : undefined}
            ref={register({ required: translate('name_empty') })}
          />

          <Input
            name="clientWebhook"
            label={translate('client_webhook')}
            autoComplete="off"
            type="text"
            info={errors.clientWebhook?.message}
            required={true}
            status={errors.clientWebhook?.message ? 'error' : undefined}
            ref={register({
              required: translate('client_webhook_criteria_error'),
              validate: domainValidation,
            })}
          />
        </Form>
      </Grid>
    </SectionRenderTemplate>
  );
};

export default CreateAPI;
